/**
 * Keeps reject-reason capture consistent across asset cards and confirmation
 * modals while leaving row-specific state ownership to their parent container.
 */

import React from 'react';
import Column from '@amzn/meridian/column';
import Input from '@amzn/meridian/input';
import Select, { SelectOption } from '@amzn/meridian/select';
import { useTranslation } from 'src/arb-wrapper';
import type { RejectReason } from 'src/store/generated/leela-api-generated';
import {
  REJECT_REASON_LABEL_KEYS,
  REJECT_REASON_NOTE_MAX_LENGTH,
  REJECT_REASONS,
  type RejectReasonValue,
} from 'src/utils/rejectReason';

const OTHER_REJECT_REASON: RejectReason = 'OTHER';
const DEFAULT_TEST_ID_PREFIX = 'reject-reason';

export type RejectReasonSelection = RejectReasonValue;

export interface RejectReasonSelectProps {
  readonly value: RejectReasonSelection;
  readonly testIdPrefix?: string;
  readonly onChange: (value: RejectReasonSelection) => void;
}

function getTestId (prefix: string, suffix: string): string {
  return `${prefix}-${suffix}`;
}

export const RejectReasonSelect: React.FC<RejectReasonSelectProps> = ({
  value,
  testIdPrefix = DEFAULT_TEST_ID_PREFIX,
  onChange,
}) => {
  const { t } = useTranslation();
  const note = value.note ?? '';
  const trimmedNoteLength = note.trim().length;

  const handleReasonChange = (reason: RejectReason): void => {
    onChange({
      reason,
      note:
        reason === OTHER_REJECT_REASON && value.reason === OTHER_REJECT_REASON
          ? note
          : undefined,
    });
  };

  const handleNoteChange = (nextNote: string): void => {
    onChange({
      reason: OTHER_REJECT_REASON,
      note: nextNote,
    });
  };

  const handleNoteBlur = (): void => {
    const trimmedNote = note.trim();
    if (trimmedNote !== note) {
      onChange({ reason: OTHER_REJECT_REASON, note: trimmedNote });
    }
  };

  return (
    <Column data-testid={testIdPrefix} spacing="200" width="100%">
      <Select
        aria-label={t('asset_reject_reason_select_hint')}
        data-testid={getTestId(testIdPrefix, 'select')}
        placeholder={t('asset_reject_reason_select_hint')}
        value={value.reason}
        width="100%"
        onChange={handleReasonChange}
      >
        {REJECT_REASONS.map((reason) => (
          <SelectOption
            data-testid={getTestId(testIdPrefix, `option-${reason}`)}
            key={reason}
            label={t(REJECT_REASON_LABEL_KEYS[reason])}
            value={reason}
          />
        ))}
      </Select>
      {value.reason === OTHER_REJECT_REASON && (
        <Input
          constraintText={t('asset_reject_reason_note_guidance', {
            count: trimmedNoteLength,
            max: REJECT_REASON_NOTE_MAX_LENGTH,
          })}
          data-testid={getTestId(testIdPrefix, 'note')}
          label={t('asset_reject_reason_note_label')}
          required={true}
          value={note}
          width="100%"
          onBlur={handleNoteBlur}
          onChange={handleNoteChange}
        />
      )}
    </Column>
  );
};
